// Reads what THIS device will actually do with a push, so the backend can turn
// it into a metric.
//
// Why the device has to be the one to say: every server-side signal stops at
// "FCM accepted the message". Whether a human is then alerted is decided
// afterwards, inside the phone, by settings the server cannot see — and the two
// complaints operators get ("the owner never got it", "it arrived but was
// silent") are both decided there. Reporting these few fields is the difference
// between an outage that has to be diagnosed by reading source code and one that
// is a labelled bar on a dashboard.
//
// Everything here is best-effort: any failure degrades to "field omitted", never
// to a thrown error. This runs on the login path — it must never be able to stop
// a device from registering for push.

import { Capacitor, registerPlugin } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { buildInfo } from '@/env';
import {
  androidHealthFields,
  type AndroidHealthFields,
  type AndroidNotificationSettings,
} from '@/features/notifications/lib/androidNotificationSettings';
import {
  iosHealthFields,
  type IosHealthFields,
  type IosNotificationSettings,
} from '@/features/notifications/lib/iosNotificationSettings';

/** Matches DeviceHealthSchema in @restaurant/api-contracts. */
export interface DeviceHealth extends IosHealthFields, AndroidHealthFields {
  permission?: 'granted' | 'denied' | 'prompt';
  channelImportance?: number;
  channelSound?: boolean;
  appVersion?: string;
  osVersion?: string;
}

// The channel the backend routes every alert to (see push.service.ts). Its
// settings ARE the android alert sound — post-8.0 the payload cannot override
// them, and a channel cannot be modified after creation, so reading it back is
// the only way to know whether this device will ring.
const ALERT_CHANNEL_ID = 'alerts';

// Android IMPORTANCE_HIGH. At or above this the notification shows a heads-up
// banner and plays the channel sound; below it, it slips silently into the shade.
const IMPORTANCE_HIGH = 4;

interface NotificationSettingsPlugin {
  getSettings(): Promise<IosNotificationSettings | AndroidNotificationSettings>;
}

const NotificationSettings = registerPlugin<NotificationSettingsPlugin>('NotificationSettings');

export async function collectDeviceHealth(): Promise<DeviceHealth> {
  const health: DeviceHealth = {};

  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    // The plugin reports 'prompt-with-rationale' as a third value; both prompt
    // variants mean the same thing to us — not decided yet.
    health.permission = receive === 'granted' || receive === 'denied' ? receive : 'prompt';
  } catch {
    /* plugin unavailable — leave unreported rather than guessing */
  }

  if (Capacitor.getPlatform() === 'android') {
    try {
      const { channels } = await FirebaseMessaging.listChannels();
      const alerts = channels.find((c) => c.id === ALERT_CHANNEL_ID);
      // Absent → the fields stay undefined, which the backend reads as
      // `channel_missing`. Deliberately NOT reported as importance 0: a channel
      // that was never created and one the user switched off need different
      // fixes (reinstall vs. re-enable in settings).
      if (alerts) {
        health.channelImportance = alerts.importance ?? IMPORTANCE_HIGH;
        // A channel whose sound did not resolve is the OTA trap: a JS bundle
        // creating the channel on an older native build that has no res/raw
        // clip. It looks configured and is permanently mute.
        health.channelSound = Boolean(alerts.sound);
        if (typeof alerts.visibility === 'number') health.channelVisibility = alerts.visibility;
      }
    } catch {
      /* listChannels is android-only and can throw on old plugin builds */
    }
  }

  const platform = Capacitor.getPlatform();
  if (platform === 'ios' || platform === 'android') {
    const settings = await NotificationSettings.getSettings().catch(() => null);
    if (settings && platform === 'ios') {
      Object.assign(health, iosHealthFields(settings as IosNotificationSettings));
    }
    if (settings && platform === 'android') {
      Object.assign(health, androidHealthFields(settings as AndroidNotificationSettings));
    }
  }

  // Baked at build time. On Capgo the OTA bundle can be newer than the installed
  // native shell, and that mismatch is precisely what creates a broken alerts
  // channel — so the running bundle's version is the one worth reporting.
  const appVersion = buildInfo.appVersion || buildInfo.gitSha;
  if (appVersion) health.appVersion = appVersion.slice(0, 50);

  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    health.osVersion = navigator.userAgent.slice(0, 50);
  }

  return health;
}
